/**
 * 应收报表（应收管理）分页查询
 * 参数 / 返回字段与应收客户一致，仅 GraphQL 根节点不同
 */
export type {
  ReceivableCustomerItem as ReceivableManageItem,
  ReceivableCustomerQueryParams as ReceivableManageQueryParams,
  ReceivableCustomerRawItem as ReceivableManageRawItem,
} from '../receivableCustomer';

import type {
  ReceivableCustomerItem,
  ReceivableCustomerQueryParams,
  ReceivableCustomerRawItem,
} from '../receivableCustomer';

interface ReceivableManageQueryResponse {
  data?: {
    ariesPlatformReceivableManageQuery?: {
      queryPage?: {
        content?: ReceivableCustomerRawItem[];
        totalElements?: number | string;
        totalPages?: number | string;
        size?: number | string;
        currentPage?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function emptyToBlank(value?: string) {
  return (value ?? '').trim();
}

function buildReceivableQueryData(params: ReceivableCustomerQueryParams) {
  const data: Record<string, string | boolean> = {
    salesOfficeRegionCode: emptyToBlank(params.salesOfficeRegionCode),
    saleOfficeCode: emptyToBlank(params.saleOfficeCode),
    sapCustomerCode: emptyToBlank(params.sapCustomerCode),
    financeCode: emptyToBlank(params.financeCode),
    salesmanUserCode: emptyToBlank(params.salesmanUserCode),
    dissaleChannelCode: emptyToBlank(params.dissaleChannelCode),
    feedbackStatus: emptyToBlank(params.feedbackStatus),
    riskLevel: emptyToBlank(params.riskLevel),
    controlUserCode: emptyToBlank(params.controlUserCode),
  };
  if (typeof params.hasControlUser === 'boolean') {
    data.hasControlUser = params.hasControlUser;
  }
  if (typeof params.isProsecute === 'boolean') {
    data.isProsecute = params.isProsecute;
  }
  if (typeof params.isOverdue === 'boolean') {
    data.isOverdue = params.isOverdue;
  }
  return data;
}

function formatChannel(code?: string, name?: string) {
  if (name) return name;
  if (code === '10') return '10渠道';
  if (code === '20') return '20渠道';
  return code || '';
}

function isRiskManualModified(value?: number | string | boolean) {
  return value === 1 || value === '1' || value === true;
}

function formatYesNo(value?: number | string | boolean) {
  if (value === 1 || value === '1' || value === true) return '是';
  if (value === 0 || value === '0' || value === false) return '否';
  return '-';
}

function mapReceivableManageItem(raw: ReceivableCustomerRawItem): ReceivableCustomerItem {
  return {
    id: String(raw.id ?? ''),
    masterId: raw.masterId != null && raw.masterId !== '' ? String(raw.masterId) : undefined,
    status: raw.feedbackStatus,
    latestFeedback: raw.feedbackMessage,
    latestFeedbackBy: raw.feedbackUserName,
    latestFeedbackAt: raw.feedbackDate,
    isProfessional: formatYesNo(raw.isProfessional),
    financeArea: raw.salesOfficeRegionName,
    corpCode: raw.corpCode,
    companyName: raw.saleOfficeName,
    saleChannelName: raw.saleChannelName,
    channel: formatChannel(raw.dissaleChannelCode, raw.dissaleChannelName),
    dissaleChannelCode: raw.dissaleChannelCode,
    controller: raw.controlUserCode,
    customerCode: raw.sapCustomerCode || raw.custCode,
    customerName: raw.custName,
    paymentTerm: raw.paymentConditionName,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
    riskLevel: raw.riskLevel,
    riskReason: raw.riskReason,
    overdueReason: raw.riskDesc,
    isProsecute: formatYesNo(raw.isProsecute),
    riskManualModified: isRiskManualModified(raw.riskManualModified),
    financeOwner: raw.financeName,
    financeOwnerNo: raw.financeCode,
    salesman: raw.salesmanUserName,
    salesmanNo: raw.salesmanUserCode,
    companyManager: raw.managerName,
    companyManagerNo: raw.managerCode,
    syncAt: raw.lDate,
    // 弹窗展示用后端已冲抵结果，避免前端再次冲抵
    dqysJson: raw.dqysDisplayJson || raw.dqysJson,
  };
}

function buildReceivableManageGraphql(
  currentPage: number,
  size: number,
  queryData: string,
) {
  return `{
  ariesPlatformReceivableManageQuery {
    queryPage(
      page: {currentPage: ${currentPage}, size: ${size}}
      queryWrapper: {queryData: ${JSON.stringify(queryData)}}
    ) {
      content {
        id
        masterId
        dissaleChannelCode
        sapCustomerCode
        riskLevel
        riskReason
        riskDesc
        riskManualModified
        isProfessional
        isProsecute
        feedbackStatus
        feedbackMessage
        feedbackUserName
        feedbackDate
        custCode
        custName
        saleOfficeCode
        saleOfficeName
        corpCode
        saleChannelName
        dissaleChannelName
        controlUserName
        controlUserCode
        salesmanUserCode
        salesmanUserName
        paymentConditionName
        remainingReceivableAmt
        xyqxzCny
        yqTotalCny
        yqMonth01Cny
        yqMonth02Cny
        yqMonth03Cny
        yqMonth04Cny
        yqMonth05Cny
        yqMonth06Cny
        yqMonth07Cny
        yqMonth08Cny
        yqMonth09Cny
        yqMonth10Cny
        yqMonth11Cny
        yqMonth12Cny
        yqMonth13Cny
        dwsUpdateTime
        lDate
        salesOfficeRegionCode
        salesOfficeRegionName
        financeCode
        financeName
        managerCode
        managerName
        dqysJson
        dqysDisplayJson
      }
      totalElements
      totalPages
      size
      currentPage
    }
  }
}`;
}

/**
 * 应收报表分页查询
 */
export async function queryReceivableManagePage(
  params: ReceivableCustomerQueryParams,
): Promise<{ content: ReceivableCustomerItem[]; totalElements: number }> {
  const queryData = JSON.stringify(buildReceivableQueryData(params));
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildReceivableManageGraphql(params.currentPage, params.size, queryData),
    }),
  });

  if (!response.ok) {
    throw new Error(`应收报表接口请求失败: ${response.status}`);
  }

  const result: ReceivableManageQueryResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '应收报表 GraphQL 请求失败');
  }

  const pageData = result.data?.ariesPlatformReceivableManageQuery?.queryPage;
  return {
    content: (pageData?.content ?? []).map(mapReceivableManageItem),
    totalElements: Number(pageData?.totalElements ?? 0),
  };
}

/** 统计接口原始字段（金额合计） */
interface ReceivableManageStatisticalRaw {
  remainingReceivableAmt?: number | string;
  xyqxzCny?: number | string;
  yqTotalCny?: number | string;
  yqMonth01Cny?: number | string;
  yqMonth02Cny?: number | string;
  yqMonth03Cny?: number | string;
  yqMonth04Cny?: number | string;
  yqMonth05Cny?: number | string;
  yqMonth06Cny?: number | string;
  yqMonth07Cny?: number | string;
  yqMonth08Cny?: number | string;
  yqMonth09Cny?: number | string;
  yqMonth10Cny?: number | string;
  yqMonth11Cny?: number | string;
  yqMonth12Cny?: number | string;
  yqMonth13Cny?: number | string;
}

interface ReceivableManageStatisticalResponse {
  data?: {
    ariesPlatformReceivableManageQuery?: {
      queryStatisticalData?: ReceivableManageStatisticalRaw | null;
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 列表筛选条件（不含分页） */
export type ReceivableManageFilterParams = Omit<
  ReceivableCustomerQueryParams,
  'currentPage' | 'size'
>;

function buildManageStatisticalDataGraphql(params: ReceivableManageFilterParams) {
  const data = buildReceivableQueryData({
    ...params,
    currentPage: 1,
    size: 1,
  });
  const lines = Object.entries(data).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return `${key}: ${value}`;
    }
    return `${key}: "${escapeGraphqlString(String(value))}"`;
  });

  return `{
  ariesPlatformReceivableManageQuery {
    queryStatisticalData(
      data: {
        ${lines.join('\n        ')}
      }
    ) {
      remainingReceivableAmt
      xyqxzCny
      yqTotalCny
      yqMonth01Cny
      yqMonth02Cny
      yqMonth03Cny
      yqMonth04Cny
      yqMonth05Cny
      yqMonth06Cny
      yqMonth07Cny
      yqMonth08Cny
      yqMonth09Cny
      yqMonth10Cny
      yqMonth11Cny
      yqMonth12Cny
      yqMonth13Cny
    }
  }
}`;
}

function mapManageStatisticalToSummaryRow(
  raw: ReceivableManageStatisticalRaw,
): ReceivableCustomerItem {
  return {
    id: '__receivable_manage_summary__',
    isSummaryRow: true,
    receivableBalance: raw.remainingReceivableAmt,
    creditWithin: raw.xyqxzCny,
    overdueTotal: raw.yqTotalCny,
    overdue1: raw.yqMonth01Cny,
    overdue2: raw.yqMonth02Cny,
    overdue3: raw.yqMonth03Cny,
    overdue4: raw.yqMonth04Cny,
    overdue5: raw.yqMonth05Cny,
    overdue6: raw.yqMonth06Cny,
    overdue7: raw.yqMonth07Cny,
    overdue8: raw.yqMonth08Cny,
    overdue9: raw.yqMonth09Cny,
    overdue10: raw.yqMonth10Cny,
    overdue11: raw.yqMonth11Cny,
    overdue12: raw.yqMonth12Cny,
    overdueOverYear: raw.yqMonth13Cny,
  };
}

/**
 * 应收报表统计合计（与列表相同筛选条件）
 */
export async function queryReceivableManageStatisticalData(
  params: ReceivableManageFilterParams,
): Promise<ReceivableCustomerItem | null> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildManageStatisticalDataGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`应收报表统计接口请求失败: ${response.status}`);
  }

  const result: ReceivableManageStatisticalResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '应收报表统计 GraphQL 请求失败');
  }

  const raw = result.data?.ariesPlatformReceivableManageQuery?.queryStatisticalData;
  if (!raw) return null;
  return mapManageStatisticalToSummaryRow(raw);
}

/** 编辑风险字段入参 */
export interface SaveReceivableManageParams {
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
  /** 业务日期 yyyy-MM-dd */
  lDate: string;
  riskLevel: string;
  riskReason: string;
  riskDesc: string;
  /** 是否起诉客户 */
  isProsecute: boolean;
}

interface SaveReceivableManageResponse {
  data?: {
    ariesPlatformReceivableManageMutation?: {
      saveReceivableManage?: {
        dissaleChannelCode?: string;
        sapCustomerCode?: string;
        lDate?: string;
        riskLevel?: string;
        riskReason?: string;
        riskDesc?: string;
        isProsecute?: boolean;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

function escapeGraphqlString(value: string) {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

function buildSaveReceivableManageMutation(params: SaveReceivableManageParams) {
  return `mutation {
  ariesPlatformReceivableManageMutation {
    saveReceivableManage(
      data: {
        dissaleChannelCode: "${escapeGraphqlString(params.dissaleChannelCode)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        corpCode: "${escapeGraphqlString(params.corpCode)}"
        lDate: "${escapeGraphqlString(params.lDate)}"
        riskLevel: "${escapeGraphqlString(params.riskLevel)}"
        riskReason: "${escapeGraphqlString(params.riskReason)}"
        riskDesc: "${escapeGraphqlString(params.riskDesc)}"
        isProsecute: ${params.isProsecute}
      }
    ) {
      dissaleChannelCode
      sapCustomerCode
      corpCode
      lDate
      riskLevel
      riskReason
      riskDesc
      isProsecute
    }
  }
}`;
}

/**
 * 保存应收报表风险字段
 */
export async function saveReceivableManage(params: SaveReceivableManageParams) {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildSaveReceivableManageMutation(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`保存风险字段请求失败: ${response.status}`);
  }

  const result: SaveReceivableManageResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '保存风险字段失败');
  }

  const saved = result.data?.ariesPlatformReceivableManageMutation?.saveReceivableManage;
  if (!saved) {
    throw new Error('保存风险字段失败：无返回数据');
  }
  return saved;
}

/** 风险修改日志 */
export interface OverdueRiskRecordLogItem {
  id?: string;
  dissaleChannelCode?: string;
  sapCustomerCode?: string;
  /** 修改前风险等级 */
  originRiskLevel?: string;
  riskLevel?: string;
  riskReason?: string;
  riskDesc?: string;
  /** 是否起诉客户 */
  isProsecute?: number | string | boolean;
  manualModified?: boolean | string;
  createDate?: string;
  createName?: string;
  createWorkCode?: string;
}

export interface QueryRiskRecordLogParams {
  dissaleChannelCode: string;
  sapCustomerCode: string;
  /** 公司编码 */
  corpCode: string;
}

interface QueryRiskRecordLogResponse {
  data?: {
    ariesPlatformReceivableManageQuery?: {
      overdueRiskRecordLog?: OverdueRiskRecordLogItem[];
    };
  };
  errors?: Array<{ message?: string }>;
}

function buildRiskRecordLogGraphql(params: QueryRiskRecordLogParams) {
  return `{
  ariesPlatformReceivableManageQuery {
    overdueRiskRecordLog(
      data: {
        dissaleChannelCode: "${escapeGraphqlString(params.dissaleChannelCode)}"
        sapCustomerCode: "${escapeGraphqlString(params.sapCustomerCode)}"
        corpCode: "${escapeGraphqlString(params.corpCode)}"
      }
    ) {
      id
      dissaleChannelCode
      sapCustomerCode
      corpCode
      originRiskLevel
      riskLevel
      riskReason
      riskDesc
      isProsecute
      manualModified
      createDate
      createName
      createWorkCode
    }
  }
}`;
}

/**
 * 查询应收报表风险修改日志
 */
export async function queryOverdueRiskRecordLog(
  params: QueryRiskRecordLogParams,
): Promise<OverdueRiskRecordLogItem[]> {
  const response = await fetch(`/pamirs/AriesCorePlatform`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'AriesCorePlatform',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildRiskRecordLogGraphql(params),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询修改日志请求失败: ${response.status}`);
  }

  const result: QueryRiskRecordLogResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询修改日志失败');
  }

  return result.data?.ariesPlatformReceivableManageQuery?.overdueRiskRecordLog ?? [];
}

/** 应收报表导出 model（与 Excel 工作簿定义一致） */
export const RECEIVABLE_MANAGE_EXPORT_MODEL =
  'aries.platform.AriesPlatformReceivableManage';

/** 应收报表菜单 path 前缀（与导入/导出 variables.path 一致） */
export const RECEIVABLE_MANAGE_MENU_PATH =
  '/aries_core_platform/uiMenub8c4e2a17f394d6a9e5c0d1f2a3b4c5d';

export interface ReceivableManageToolbarAuth {
  canExport: boolean;
  canImport: boolean;
  canSendSummary: boolean;
  canSendBranch: boolean;
}

/**
 * 用原生 fetch 探测 ViewAction 是否有权限（与导入/导出一致）。
 * 不用 ViewActionCache，避免无权限时全局弹出「页面不存在或无权限访问」。
 */
async function canAccessViewAction(actionName: string): Promise<boolean> {
  try {
    const response = await fetch(`/pamirs/base`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        module: 'base',
      },
      body: JSON.stringify({
        operationName: null,
        variables: {
          needCompileView: false,
          needCompileMask: false,
          model: RECEIVABLE_MANAGE_EXPORT_MODEL,
          name: actionName,
          path: RECEIVABLE_MANAGE_MENU_PATH,
        },
        query: `query ($model: String, $name: String, $needCompileView: Boolean = false, $needCompileMask: Boolean = false) {
  viewActionQuery {
    load(
      request: {model: $model, name: $name, needCompileView: $needCompileView, needCompileMask: $needCompileMask}
    ) {
      name
    }
  }
}`,
      }),
    });
    if (!response.ok) {
      return false;
    }
    const result = await response.json();
    if (result?.errors?.length) {
      return false;
    }
    return !!result?.data?.viewActionQuery?.load?.name;
  } catch {
    return false;
  }
}

/**
 * 查询应收报表工具栏动作权限（与导入/导出相同：ViewAction + 菜单 path）。
 * 无权限时隐藏对应按钮，且不触发全局错误提示。
 */
export async function queryReceivableManageToolbarAuth(): Promise<ReceivableManageToolbarAuth> {
  const empty: ReceivableManageToolbarAuth = {
    canExport: false,
    canImport: false,
    canSendSummary: false,
    canSendBranch: false,
  };
  try {
    const [canExport, canImport, canSendSummary, canSendBranch] = await Promise.all([
      canAccessViewAction('internalGotoListExportDialog'),
      canAccessViewAction('internalGotoListImportDialog'),
      canAccessViewAction('sendSummaryEmail'),
      canAccessViewAction('sendBranchEmail'),
    ]);
    return { canExport, canImport, canSendSummary, canSendBranch };
  } catch (error) {
    console.warn('查询应收报表工具栏权限失败', error);
    return empty;
  }
}

export interface ExcelWorkbookDefinitionItem {
  id: string;
  displayName: string;
}

interface QueryExportTemplateResponse {
  data?: {
    excelWorkbookDefinitionQuery?: {
      queryPage?: {
        content?: ExcelWorkbookDefinitionItem[];
        totalElements?: number | string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/** RSQL → base64:xxx（平台导出约定） */
export function encodeRsqlBase64(rsql: string) {
  const utf8 = unescape(encodeURIComponent(rsql));
  return `base64:${btoa(utf8)}`;
}

function buildExportTemplateRsql(model: string) {
  return `(1==1) and (model == ${model} and dataStatus == 'ENABLED' and type =in= ('IMPORT_EXPORT','EXPORT'))`;
}

function buildQueryExportTemplatesGraphql(model: string) {
  const rsql = encodeRsqlBase64(buildExportTemplateRsql(model));
  return `{
  excelWorkbookDefinitionQuery {
    queryPage(
      page: {currentPage: 1, size: 50}
      queryWrapper: {rsql: "${rsql}", queryData: "{}"}
    ) {
      content {
        displayName
        id
      }
      totalPages
      totalElements
    }
  }
}`;
}

/**
 * 查询当前 model 可用的导出模板
 */
export async function queryExportTemplates(
  model: string = RECEIVABLE_MANAGE_EXPORT_MODEL,
): Promise<ExcelWorkbookDefinitionItem[]> {
  const response = await fetch(`/pamirs/file`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'file',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildQueryExportTemplatesGraphql(model),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询导出模板失败: ${response.status}`);
  }

  const result: QueryExportTemplateResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询导出模板失败');
  }

  return result.data?.excelWorkbookDefinitionQuery?.queryPage?.content ?? [];
}

/** 应收报表导出接口 variables.path（固定） */
export const RECEIVABLE_MANAGE_EXPORT_PATH =
  '/aries_core_platform/uiMenub8c4e2a17f394d6a9e5c0d1f2a3b4c5d/ACTION#aries.platform.AriesPlatformReceivableManage#internalGotoListExportDialog';

/** 应收报表导入接口 variables.path（固定） */
export const RECEIVABLE_MANAGE_IMPORT_PATH =
  '/aries_core_platform/uiMenub8c4e2a17f394d6a9e5c0d1f2a3b4c5d/ACTION#aries.platform.AriesPlatformReceivableManage#internalGotoListImportDialog';

export interface CreateExportTaskParams {
  workbookDefinitionId: string;
  /** 勾选行 masterId；有值时写入 queryData.masterIdList */
  masterIdList?: string[];
  /** 未勾选时带上列表筛选（与 queryPage 的 queryData 一致） */
  queryData?: Record<string, unknown>;
  model?: string;
  /** 请求体 variables.path；应收报表必传，客户/历史归档暂不传 */
  path?: string;
}

export interface CreateExportTaskResult {
  id?: string;
  name?: string;
  state?: string;
  file?: {
    name?: string;
    url?: string;
  };
}

interface CreateExportTaskResponse {
  data?: {
    excelExportTaskMutation?: {
      createExportTask?: CreateExportTaskResult;
    };
  };
  errors?: Array<{ message?: string }>;
}

function normalizeMasterIdList(ids?: string[]) {
  return [...new Set((ids ?? []).map((id) => String(id).trim()).filter(Boolean))];
}

function buildCreateExportTaskMutation(params: CreateExportTaskParams) {
  const model = params.model || RECEIVABLE_MANAGE_EXPORT_MODEL;
  const masterIdList = normalizeMasterIdList(params.masterIdList);
  const hasSelected = masterIdList.length > 0;
  const rsql = encodeRsqlBase64('(1==1)');

  const queryPayload = hasSelected
    ? { masterIdList }
    : (params.queryData ?? {});
  const queryDataJson = escapeGraphqlString(JSON.stringify(queryPayload));
  const conditionWrapper = `{rsql: "${rsql}", queryData: "${queryDataJson}"}`;

  return `mutation {
  excelExportTaskMutation {
    createExportTask(
      data: {
        workbookDefinition: {id: ${params.workbookDefinitionId}}
        conditionWrapper: ${conditionWrapper}
        model: "${escapeGraphqlString(model)}"
      }
    ) {
      id
      name
      state
      file {
        name
        url
      }
    }
  }
}`;
}

/**
 * 创建导出任务
 */
export async function createExportTask(
  params: CreateExportTaskParams,
): Promise<CreateExportTaskResult> {
  const body: {
    operationName: null;
    variables?: { path: string };
    query: string;
  } = {
    operationName: null,
    query: buildCreateExportTaskMutation(params),
  };
  const path = (params.path || '').trim();
  if (path) {
    body.variables = { path };
  }

  const response = await fetch(`/pamirs/file`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'file',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`创建导出任务失败: ${response.status}`);
  }

  const result: CreateExportTaskResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '创建导出任务失败');
  }

  const task = result.data?.excelExportTaskMutation?.createExportTask;
  if (!task) {
    throw new Error('创建导出任务失败：无返回数据');
  }
  return task;
}

function buildImportTemplateRsql(model: string) {
  return `(1==1) and (model == ${model} and dataStatus == 'ENABLED' and type =in= ('IMPORT_EXPORT','IMPORT'))`;
}

function buildQueryImportTemplatesGraphql(model: string) {
  const rsql = encodeRsqlBase64(buildImportTemplateRsql(model));
  return `{
  excelWorkbookDefinitionQuery {
    queryPage(
      page: {currentPage: 1, size: 50}
      queryWrapper: {rsql: "${rsql}", queryData: "{}"}
    ) {
      content {
        displayName
        id
      }
      totalPages
      totalElements
    }
  }
}`;
}

/**
 * 查询当前 model 可用的导入模板
 */
export async function queryImportTemplates(
  model: string = RECEIVABLE_MANAGE_EXPORT_MODEL,
): Promise<ExcelWorkbookDefinitionItem[]> {
  const response = await fetch(`/pamirs/file`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'file',
    },
    body: JSON.stringify({
      operationName: null,
      query: buildQueryImportTemplatesGraphql(model),
    }),
  });

  if (!response.ok) {
    throw new Error(`查询导入模板失败: ${response.status}`);
  }

  const result: QueryExportTemplateResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '查询导入模板失败');
  }

  return result.data?.excelWorkbookDefinitionQuery?.queryPage?.content ?? [];
}

interface FetchImportTemplateUrlResponse {
  data?: {
    excelWorkbookDefinitionQuery?: {
      fetchImportTemplateUrl?: {
        redirectUri?: string;
      };
    };
  };
  errors?: Array<{ message?: string }>;
}

/** 与后端 AriesPlatformExcelWorkbookDefinitionAction 约定的查询条件前缀 */
const IMPORT_QUERY_PREFIX = '__IMPORT_QUERY__';

export interface FetchImportTemplateOptions {
  /** 勾选行 masterId；有值时只填勾选数据 */
  masterIdList?: string[];
  /** 未勾选时带上列表筛选（与导出 queryData 一致） */
  queryData?: Record<string, unknown>;
}

/**
 * 获取导入模板下载地址。
 * 有勾选填勾选行，无勾选按查询条件填。
 */
export async function fetchImportTemplateUrl(
  workbookDefinitionId: string,
  options?: FetchImportTemplateOptions,
): Promise<string> {
  const masterIdList = normalizeMasterIdList(options?.masterIdList);
  const queryPayload = masterIdList.length > 0 ? { masterIdList } : (options?.queryData ?? {});
  // base64，避免 { } " 写进 GraphQL 导致解析失败、接口不返回下载地址
  const definitionContext = escapeGraphqlString(
    IMPORT_QUERY_PREFIX + encodeRsqlBase64(JSON.stringify(queryPayload)),
  );
  const query = `{
  excelWorkbookDefinitionQuery {
    fetchImportTemplateUrl(data: {id: ${workbookDefinitionId}, definitionContext: "${definitionContext}"}) {
      redirectUri
    }
  }
}`;

  const response = await fetch(`/pamirs/file`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'file',
    },
    body: JSON.stringify({
      operationName: null,
      query,
    }),
  });

  if (!response.ok) {
    throw new Error(`获取导入模板失败: ${response.status}`);
  }

  const result: FetchImportTemplateUrlResponse = await response.json();
  if (result.errors?.length) {
    throw new Error(result.errors[0]?.message || '获取导入模板失败');
  }

  const uri = result.data?.excelWorkbookDefinitionQuery?.fetchImportTemplateUrl?.redirectUri;
  if (!uri) {
    throw new Error('获取导入模板失败：无下载地址');
  }
  return uri;
}

export interface CreateImportTaskParams {
  workbookDefinitionId: string;
  fileId: string;
  fileUrl: string;
  /** 请求体 variables.path；应收报表必传 */
  path?: string;
}

export interface CreateImportTaskResult {
  name?: string;
  state?: string;
  /** state=FAILURE 时的业务提示 */
  errorMessage?: string;
}

interface CreateImportTaskGraphqlError {
  message?: string;
  extensions?: {
    messages?: Array<{ message?: string }>;
  };
}

interface CreateImportTaskResponse {
  data?: {
    excelImportTaskMutation?: {
      createImportTask?: {
        name?: string;
        state?: string;
      };
    };
  };
  errors?: CreateImportTaskGraphqlError[];
}

function extractImportBizMessage(errors?: CreateImportTaskGraphqlError[]) {
  for (const err of errors ?? []) {
    const nested = err.extensions?.messages?.[0]?.message;
    if (nested) return nested;
    if (err.message) return err.message;
  }
  return '导入失败';
}

/**
 * 创建导入任务（按返回 state 判断成败；FAILURE 时带上业务文案）
 */
export async function createImportTask(
  params: CreateImportTaskParams,
): Promise<CreateImportTaskResult> {
  const query = `mutation {
  excelImportTaskMutation {
    createImportTask(
      data: {
        workbookDefinition: {id: ${params.workbookDefinitionId}}
        file: {
          id: "${escapeGraphqlString(params.fileId)}"
          url: "${escapeGraphqlString(params.fileUrl)}"
        }
      }
    ) {
      name
      state
    }
  }
}`;

  const body: {
    operationName: null;
    variables?: { path: string };
    query: string;
  } = {
    operationName: null,
    query,
  };
  const path = (params.path || '').trim();
  if (path) {
    body.variables = { path };
  }

  const response = await fetch(`/pamirs/file`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      module: 'file',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`创建导入任务失败: ${response.status}`);
  }

  const result: CreateImportTaskResponse = await response.json();
  const task = result.data?.excelImportTaskMutation?.createImportTask;
  const bizMessage = extractImportBizMessage(result.errors);

  if (!task) {
    throw new Error(bizMessage || '创建导入任务失败：无返回数据');
  }

  const state = String(task.state ?? '').toUpperCase();
  if (state === 'FAILURE') {
    return {
      name: task.name,
      state: task.state,
      errorMessage: bizMessage,
    };
  }

  return {
    name: task.name,
    state: task.state,
  };
}
